/* eslint-disable indent */
/**
 * HealthyHome eligibility step component.
 *
 * Mirrors the HEA eligibility flow with two questions removed:
 *   - Q8: gasProvider  (not asked for HealthyHome)
 *   - Q9: electricProvider  (not asked for HealthyHome)
 *
 * `howHeated` is therefore the final question. Completion is determined
 * by `isHealthyHomeEligibilityComplete` (src/utils/isHealthyHomeEligibilityComplete.js).
 *
 * Entry point: navigate to /HealthyHome — App.jsx detects the pathname and
 * pre-sets `path = 'healthyhome'`, skipping ChoosePath (step 0).
 */
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useFormContext } from '@context/FormContext'
import { AnsweredStrip, QuestionCard, YNGrid } from './EligibilityShared'

/* ── Options ── */
const options = {
  boolean: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ],
  howHeated: [
    { value: 'Gas', label: 'Natural Gas', icon: '🔥', desc: "Connected to gas lines — no refueling needed." },
    { value: 'Oil', label: 'Heating Oil', icon: '🛢', desc: 'A truck comes and delivers to your home.' },
    { value: 'Propane', label: 'Propane', icon: '⛽', desc: 'A small tank is near your heating system.' },
    { value: 'Electric', label: 'Electric', icon: '⚡', desc: "Equipment connects to your home's electrical system." },
  ],
  familySize: [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5+', value: '5+' },
  ],
  ownerOrRenter: [
    { label: 'Renter', value: 'Tenant' },
    { label: 'Owner', value: 'Owner-Occupied' },
  ],
  singleOrMulti: [
    { label: 'Single Family', value: 'Single family', emoji: '🏠' },
    { label: '2-Unit', value: '2-Unit Multi-Family', emoji: '🏘' },
    { label: '3-Unit', value: '3-Unit Multi-Family', emoji: '🏢' },
    { label: '4-Unit', value: '4-Unit Multi-Family', emoji: '🏣' },
    { label: '5+ Units', value: '5+ Multi-Family', emoji: '🏬' },
  ],
}

const HealthyHome = ({ currentStepIndex, setValidationSchema }) => {
  const { values, setFieldValue } = useFormContext()
  if (!values) return null

  // Unit number refs for collecting values
  const unitInputRefs = useRef({})
  const [unitNumbersConfirmed, setUnitNumbersConfirmed] = useState(false)

  useEffect(() => {
    setValidationSchema(
      yup.object().shape({
        twoYears: yup
          .string()
          .oneOf(['yes', 'no'])
          .required('This field is required'),
        paymentAssistance: yup
          .string()
          .oneOf(['yes', 'no'])
          .when('twoYears', {
            is: 'no',
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
        singleOrMulti: yup
          .string()
          .oneOf(options.singleOrMulti.map(({ value }) => value))
          .when(['twoYears', 'paymentAssistance'], {
            is: (twoYears, paymentAssistance) => twoYears === 'no' && (paymentAssistance === 'yes' || paymentAssistance === 'no'),
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
        ownerOrRenter: yup
          .string()
          .oneOf(options.ownerOrRenter.map(({ value }) => value))
          .when(['singleOrMulti'], {
            is: (singleOrMulti) =>
              singleOrMulti && !['Single family', '5+ Multi-Family'].includes(singleOrMulti),
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
        isCondo: yup
          .string()
          .oneOf(['yes', 'no'])
          .when(['ownerOrRenter', 'singleOrMulti'], {
            is: (ownerOrRenter, singleOrMulti) =>
              (singleOrMulti && ['Single family'].includes(singleOrMulti)) ||
              ['Tenant', 'Owner-Occupied'].includes(ownerOrRenter),
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
        familySize: yup
          .string()
          .oneOf(options.familySize.map(({ value }) => value))
          .when(['singleOrMulti', 'isCondo'], {
            is: (singleOrMulti, isCondo) =>
              isCondo &&
              singleOrMulti &&
              ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(singleOrMulti),
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
        unitNumber1: yup.string().when(['singleOrMulti', 'familySize', 'isCondo'], {
          is: (singleOrMulti, familySize, isCondo) =>
            (['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(singleOrMulti) &&
              familySize && Number(familySize) >= 1) ||
            (singleOrMulti === 'Single family' && isCondo === 'yes'),
          then: yup.string().trim().required('This field is required').max(10, 'Max 10 Characters'),
          otherwise: yup.string().notRequired(),
        }),
        unitNumber2: yup.string().when(['singleOrMulti', 'familySize'], {
          is: (singleOrMulti, familySize) =>
            ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(singleOrMulti) &&
            familySize && Number(familySize) >= 2,
          then: yup.string().trim().required('This field is required').max(10, 'Max 10 Characters'),
          otherwise: yup.string().notRequired(),
        }),
        unitNumber3: yup.string().when(['singleOrMulti', 'familySize'], {
          is: (singleOrMulti, familySize) =>
            ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(singleOrMulti) &&
            familySize && Number(familySize) >= 3,
          then: yup.string().trim().required('This field is required').max(10, 'Max 10 Characters'),
          otherwise: yup.string().notRequired(),
        }),
        unitNumber4: yup.string().when(['singleOrMulti', 'familySize'], {
          is: (singleOrMulti, familySize) =>
            ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(singleOrMulti) &&
            familySize && Number(familySize) === 4,
          then: yup.string().trim().required('This field is required').max(10, 'Max 10 Characters'),
          otherwise: yup.string().notRequired(),
        }),
        // howHeated is the final question for HealthyHome — no gasProvider or electricProvider
        howHeated: yup
          .string()
          .oneOf(options.howHeated.map(({ value }) => value))
          .when(['isCondo'], {
            is: (isCondo) => ['yes', 'no'].includes(isCondo),
            then: yup.string().required('This field is required'),
            otherwise: yup.string().notRequired(),
          }),
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, values.system])

  /* ── Derived visibility conditions ── */
  const twoYearsAnswered = values.twoYears != null && values.twoYears !== ''
  const paymentAssistanceVisible = twoYearsAnswered
  const paymentAssistanceAnswered = paymentAssistanceVisible && values.paymentAssistance != null && values.paymentAssistance !== ''
  const singleOrMultiVisible = paymentAssistanceAnswered
  const singleOrMultiAnswered = singleOrMultiVisible && values.singleOrMulti != null && values.singleOrMulti !== ''

  const isMultiUnit = ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(values.singleOrMulti)
  const ownerOrRenterVisible = singleOrMultiAnswered && values.singleOrMulti && !['Single family', '5+ Multi-Family'].includes(values.singleOrMulti)
  const ownerOrRenterAnswered = ownerOrRenterVisible && values.ownerOrRenter != null && values.ownerOrRenter !== ''

  const isCondoVisible =
    singleOrMultiAnswered && (
      values.ownerOrRenter != null ||
      (values.singleOrMulti && ['Single family'].includes(values.singleOrMulti))
    )
  const isCondoAnswered = isCondoVisible && values.isCondo != null && values.isCondo !== ''

  const familySizeVisible =
    isCondoAnswered && values.isCondo != null &&
    ['2-Unit Multi-Family', '3-Unit Multi-Family', '4-Unit Multi-Family'].includes(values.singleOrMulti)
  const familySizeAnswered = familySizeVisible && values.familySize != null && values.familySize !== ''

  // Unit numbers: visible when isCondo && (single family condo OR 2-4 multi with familySize 1-4)
  const unitNumbersVisible =
    isCondoAnswered && (
      (values.singleOrMulti === 'Single family' && values.isCondo === 'yes') ||
      (isMultiUnit && ['1', '2', '3', '4'].includes(String(values.familySize)))
    )
  const unitCount = values.singleOrMulti === 'Single family' ? 1 : Number(values.familySize) || 0

  // Check if all unit numbers are filled
  const allUnitsFilled = (() => {
    if (!unitNumbersVisible || unitCount === 0) return false
    for (let k = 1; k <= unitCount; k++) {
      const val = values[`unitNumber${k}`]
      if (val == null || (typeof val === 'string' && val.trim() === '')) return false
    }
    return true
  })()

  // confirmedAndFilled: true only when user clicked "Confirm unit numbers" AND all values are still filled
  const confirmedAndFilled = unitNumbersConfirmed && allUnitsFilled

  // howHeated visible only after unit numbers are explicitly confirmed via button click
  const howHeatedVisible = (() => {
    if (!isCondoAnswered) return false
    if (values.singleOrMulti === 'Single family') {
      if (values.isCondo === 'yes') return confirmedAndFilled
      return true // single family non-condo: show howHeated after isCondo answered
    }
    if (['1', '2', '3', '4'].includes(String(values.familySize))) {
      return confirmedAndFilled
    }
    return false
  })()
  // howHeated is the final question for HealthyHome — no further questions follow

  /* ── Helpers ── */
  const setAnswer = (field, value) => {
    setFieldValue(field, value)
  }

  const editField = (field) => {
    setFieldValue(field, undefined)
  }

  /* ── Unit number handlers ── */
  const handleUnitInputChange = (k, val) => {
    setFieldValue(`unitNumber${k}`, val)
  }

  /* ── Count visible dots ── */
  const dotCount = 4 // twoYears, paymentAssistance, homeType, howHeated
  let activeDot = 0
  if (!twoYearsAnswered) activeDot = 0
  else if (!paymentAssistanceAnswered && paymentAssistanceVisible) activeDot = 1
  else if (!singleOrMultiAnswered && singleOrMultiVisible) activeDot = 2
  else activeDot = 3

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-6" style={{ animation: 'fadeUp 0.4s ease both' }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#4675ad' }}>
          Eligibility Check
        </p>
        <h1
          className="text-3xl text-stone-900 leading-tight mb-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 }}
        >
          Just a few quick questions
        </h1>
        <p className="text-stone-500 text-sm">Takes under a minute — we&apos;ll confirm your eligibility instantly.</p>
      </div>

      {/* Dot progress */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {Array.from({ length: dotCount }).map((_, i) => (
          <div
            key={i}
            className={`q-dot${i === activeDot ? ' active' : i < activeDot ? ' done' : ''}`}
          />
        ))}
      </div>

      {/* Q1: twoYears */}
      {twoYearsAnswered ? (
        <AnsweredStrip
          questionLabel="Past 2-year assessment?"
          answerLabel={values.twoYears === 'yes' ? 'Yes' : 'No'}
          answerType={values.twoYears === 'yes' ? 'yes' : 'no'}
          onEdit={() => editField('twoYears')}
        />
      ) : (
        <QuestionCard meta="Question 1">
          <div className="q-text">
            Have you had a Mass Save<sup>®</sup> Home Energy Assessment in the past 2 years?
          </div>
          <YNGrid
            onYes={() => setAnswer('twoYears', 'yes')}
            onNo={() => setAnswer('twoYears', 'no')}
            yesSub="Within 2 years"
            noSub="Or not sure"
          />
        </QuestionCard>
      )}

      {/* Q2: paymentAssistance */}
      {paymentAssistanceVisible && (
        paymentAssistanceAnswered ? (
          <AnsweredStrip
            questionLabel="Utility discount / fuel assistance?"
            answerLabel={values.paymentAssistance === 'yes' ? 'Yes' : 'No'}
            answerType={values.paymentAssistance === 'yes' ? 'yes' : 'no'}
            onEdit={() => editField('paymentAssistance')}
          />
        ) : (
          <QuestionCard meta="Question 2">
            <div className="q-text">
              Are you on a utility discount rate?{' '}
              <span style={{ fontWeight: 400, fontSize: 14 }}>(R2 or R4 on your utility bill)</span>
            </div>
            <div className="q-hint">Examples: LIHEAP, fuel/heating assistance, income-based utility discount.</div>
            <YNGrid
              onYes={() => setAnswer('paymentAssistance', 'yes')}
              onNo={() => setAnswer('paymentAssistance', 'no')}
            />
          </QuestionCard>
        )
      )}

      {/* Q3: singleOrMulti */}
      {singleOrMultiVisible && (
        singleOrMultiAnswered ? (
          <AnsweredStrip
            questionLabel="Home type"
            answerLabel={options.singleOrMulti.find((o) => o.value === values.singleOrMulti)?.label || values.singleOrMulti}
            answerType="other"
            onEdit={() => editField('singleOrMulti')}
          />
        ) : (
          <QuestionCard meta="Question 3">
            <div className="q-text">What type of home do you live in?</div>
            <div className="mc-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 14 }}>
              {options.singleOrMulti.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`mc-btn${values.singleOrMulti === opt.value ? ' selected' : ''}`}
                  onClick={() => setAnswer('singleOrMulti', opt.value)}
                >
                  <div className="mc-emoji">{opt.emoji}</div>
                  <div className="mc-label">{opt.label}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q4: ownerOrRenter (for multi-unit 2-4) */}
      {ownerOrRenterVisible && (
        ownerOrRenterAnswered ? (
          <AnsweredStrip
            questionLabel="Tenant or landlord?"
            answerLabel={values.ownerOrRenter === 'Tenant' ? 'Renter' : 'Owner'}
            answerType="other"
            onEdit={() => editField('ownerOrRenter')}
          />
        ) : (
          <QuestionCard meta="Home Ownership">
            <div className="q-text">Are you the tenant or landlord?</div>
            <div className="yn-grid">
              {options.ownerOrRenter.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`yn-btn${values.ownerOrRenter === opt.value ? ' sel-yes' : ''}`}
                  onClick={() => setAnswer('ownerOrRenter', opt.value)}
                >
                  <div className="yn-label">{opt.label}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q5: isCondo */}
      {isCondoVisible && (
        isCondoAnswered ? (
          <AnsweredStrip
            questionLabel="Condo association?"
            answerLabel={values.isCondo === 'yes' ? 'Yes' : 'No'}
            answerType={values.isCondo === 'yes' ? 'yes' : 'no'}
            onEdit={() => editField('isCondo')}
          />
        ) : (
          <QuestionCard meta="Condo Status">
            <div className="q-text">Are you part of a condo association?</div>
            <YNGrid
              onYes={() => setAnswer('isCondo', 'yes')}
              onNo={() => setAnswer('isCondo', 'no')}
            />
          </QuestionCard>
        )
      )}

      {/* Q6: familySize (for multi-unit 2-4, if isCondo answered) */}
      {familySizeVisible && (
        familySizeAnswered ? (
          <AnsweredStrip
            questionLabel="Units scheduling today"
            answerLabel={values.familySize}
            answerType="other"
            onEdit={() => editField('familySize')}
          />
        ) : (
          <QuestionCard meta="Units to Schedule">
            <div className="q-text">How many units are we scheduling today?</div>
            <div className="mc-grid cols-2" style={{ marginTop: 14 }}>
              {options.familySize
                .filter((opt) => {
                  const maxUnits = parseInt(values.singleOrMulti?.[0]) || 4
                  return parseFloat(opt.value) <= maxUnits
                })
                .map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`mc-btn${values.familySize === opt.value ? ' selected' : ''}`}
                    onClick={() => setAnswer('familySize', opt.value)}
                  >
                    <div className="mc-label">{opt.label}</div>
                  </button>
                ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Unit number inputs */}
      {unitNumbersVisible && !confirmedAndFilled && (
        <QuestionCard meta="Unit Numbers">
          <div className="q-text">Enter the unit number(s) you&apos;d like to schedule:</div>
          <div className="unit-section" style={{ marginTop: 12 }}>
            <div className="unit-section-title">
              Please enter each unit number (required before proceeding)
            </div>
            <div className="unit-inputs">
              {Array.from({ length: unitCount }).map((_, i) => {
                const k = i + 1
                return (
                  <input
                    key={k}
                    ref={(el) => { unitInputRefs.current[k] = el }}
                    type="text"
                    maxLength={10}
                    placeholder={`Unit ${k}`}
                    className="unit-input"
                    value={values[`unitNumber${k}`] || ''}
                    onChange={(e) => handleUnitInputChange(k, e.target.value)}
                  />
                )
              })}
            </div>
            <button
              type="button"
              className="unit-confirm-btn"
              disabled={!allUnitsFilled}
              onClick={() => setUnitNumbersConfirmed(true)}
            >
              Confirm unit numbers →
            </button>
          </div>
        </QuestionCard>
      )}

      {/* Answered unit numbers summary */}
      {unitNumbersVisible && confirmedAndFilled && (
        <AnsweredStrip
          questionLabel={`Unit number${unitCount > 1 ? 's' : ''}`}
          answerLabel={Array.from({ length: unitCount }).map((_, i) => values[`unitNumber${i + 1}`]).join(', ')}
          answerType="other"
          onEdit={() => {
            for (let k = 1; k <= unitCount; k++) {
              setFieldValue(`unitNumber${k}`, undefined)
            }
            setUnitNumbersConfirmed(false)
          }}
        />
      )}

      {/* Q7: howHeated — final question for HealthyHome (no gas/electric provider follows) */}
      {howHeatedVisible && (
        <QuestionCard meta="Heating Fuel">
          <div className="q-text">How is your home heated?</div>
          <div className="fuel-grid">
            {options.howHeated.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`fuel-card${values.howHeated === opt.value ? ' selected' : ''}`}
                onClick={() => setAnswer('howHeated', opt.value)}
              >
                <div className="fuel-icon">{opt.icon}</div>
                <div className="fuel-label">{opt.label}</div>
                <div className="fuel-desc">{opt.desc}</div>
              </button>
            ))}
          </div>
        </QuestionCard>
      )}
    </div>
  )
}

HealthyHome.info = {
  title: 'Eligibility',
  key: 'eligibility',
}

HealthyHome.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setValidationSchema: PropTypes.func.isRequired,
  values: PropTypes.object,
}

export default HealthyHome
